import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Star, Copy, Trash2, Edit2, Check, X, ChevronRight,
  FileText, Settings, RefreshCw, Layers, Eye, Users, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const API_URL = getApiUrl();

// Composant pour afficher une carte de template
function TemplateCard({ template, onSelect, onSetDefault, onDuplicate, onDelete, isSelected }) {
  const [showMenu, setShowMenu] = useState(false);
  
  return (
    <div 
      className={`relative bg-slate-800/50 rounded-xl border transition-all cursor-pointer group ${
        isSelected 
          ? 'border-purple-500 ring-2 ring-purple-500/30' 
          : 'border-slate-700/50 hover:border-slate-600'
      }`}
      onClick={() => onSelect(template)}
    >
      {/* Badge par défaut */}
      {template.is_default && (
        <div className="absolute -top-2 -right-2 bg-yellow-500 rounded-full p-1">
          <Star size={12} className="text-white fill-white" />
        </div>
      )}
      
      {/* Header coloré */}
      <div className={`p-3 rounded-t-xl bg-gradient-to-r from-${template.header?.gradient_from || 'purple-600'} to-${template.header?.gradient_to || 'pink-600'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              {template.type === 'principal' ? (
                <FileText size={16} className="text-white" />
              ) : (
                <Layers size={16} className="text-white" />
              )}
            </div>
            <div>
              <h4 className="font-medium text-white text-sm">{template.name}</h4>
              <span className="text-xs text-white/70 capitalize">{template.type}</span>
            </div>
          </div>
          
          {/* Menu d'actions */}
          <div className="relative">
            <button 
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white"
            >
              <Settings size={14} />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 top-8 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10 py-1 min-w-40">
                {!template.is_default && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onSetDefault(template); setShowMenu(false); }}
                    className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                  >
                    <Star size={14} />
                    Définir par défaut
                  </button>
                )}
                <button 
                  onClick={(e) => { e.stopPropagation(); onDuplicate(template); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-2"
                >
                  <Copy size={14} />
                  Dupliquer
                </button>
                {!template.is_default && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(template); setShowMenu(false); }}
                    className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                  >
                    <Trash2 size={14} />
                    Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Contenu */}
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{template.sections?.length || 0} sections</span>
          <span>{template.dynamic_tabs?.filter(t => t.enabled).length || 0} onglets</span>
        </div>
        
        {/* Aperçu des sections */}
        <div className="flex flex-wrap gap-1">
          {template.sections?.slice(0, 3).map(section => (
            <span key={section.id} className="px-2 py-0.5 bg-slate-700/50 rounded text-xs text-slate-300">
              {section.icon} {section.label?.substring(0, 15)}...
            </span>
          ))}
          {(template.sections?.length || 0) > 3 && (
            <span className="px-2 py-0.5 bg-slate-700/50 rounded text-xs text-slate-400">
              +{template.sections.length - 3}
            </span>
          )}
        </div>
      </div>
      
      {/* Footer - Date de modification */}
      <div className="px-3 py-2 border-t border-slate-700/50 text-xs text-slate-500">
        {template.updated_at ? `Modifié le ${new Date(template.updated_at).toLocaleDateString('fr-FR')}` : 'Non modifié'}
      </div>
    </div>
  );
}

// Modal de création de template
function CreateTemplateModal({ isOpen, onClose, onCreate, templateType }) {
  const [name, setName] = useState('');
  const [type, setType] = useState(templateType || 'principal');
  
  const handleCreate = () => {
    if (!name.trim()) {
      toast.error('Veuillez entrer un nom');
      return;
    }
    onCreate({ name, type });
    setName('');
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700">
        <div className="p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Plus size={20} className="text-purple-400" />
            Nouveau Template
          </h3>
        </div>
        
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Nom du template *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Modal Plomberie, Modal Chauffage..."
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500"
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm text-slate-400 mb-2">Type de modal</label>
            <div className="flex gap-2">
              <button
                onClick={() => setType('principal')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                  type === 'principal' 
                    ? 'border-purple-500 bg-purple-500/20 text-white' 
                    : 'border-slate-600 bg-slate-700/30 text-slate-400 hover:border-slate-500'
                }`}
              >
                <FileText size={20} className="mx-auto mb-1" />
                <span className="text-sm">Principal</span>
              </button>
              <button
                onClick={() => setType('pipeline')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                  type === 'pipeline' 
                    ? 'border-indigo-500 bg-indigo-500/20 text-white' 
                    : 'border-slate-600 bg-slate-700/30 text-slate-400 hover:border-slate-500'
                }`}
              >
                <Layers size={20} className="mx-auto mb-1" />
                <span className="text-sm">Pipeline</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg">
            Annuler
          </button>
          <button 
            onClick={handleCreate}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg flex items-center gap-2"
          >
            <Plus size={16} />
            Créer
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant principal
export default function ModalTemplatesManager({ onSelectTemplate, selectedTemplateId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'principal', 'pipeline'
  
  // Charger les templates
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/config/modal-templates`);
      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates || []);
      }
    } catch (e) {
      console.error('Erreur chargement templates:', e);
      toast.error('Erreur lors du chargement des templates');
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);
  
  // Créer un nouveau template
  const createTemplate = async ({ name, type }) => {
    try {
      const response = await authFetch(`${API_URL}/api/config/modal-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, type })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Template "${name}" créé`);
        loadTemplates();
        if (onSelectTemplate && data.template) {
          onSelectTemplate(data.template);
        }
      }
    } catch (e) {
      console.error('Erreur création template:', e);
      toast.error('Erreur lors de la création');
    }
  };
  
  // Définir par défaut
  const setDefault = async (template) => {
    try {
      const response = await authFetch(`${API_URL}/api/config/modal-templates/${template.id}/set-default`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`"${template.name}" est maintenant le template par défaut`);
        loadTemplates();
      }
    } catch (e) {
      console.error('Erreur définition par défaut:', e);
      toast.error('Erreur');
    }
  };
  
  // Dupliquer
  const duplicate = async (template) => {
    try {
      const response = await authFetch(`${API_URL}/api/config/modal-templates/${template.id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: `${template.name} (copie)` })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Template dupliqué');
        loadTemplates();
      }
    } catch (e) {
      console.error('Erreur duplication:', e);
      toast.error('Erreur');
    }
  };
  
  // Supprimer
  const deleteTemplate = async (template) => {
    if (!window.confirm(`Supprimer le template "${template.name}" ?`)) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/config/modal-templates/${template.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Template supprimé');
        loadTemplates();
      } else {
        toast.error(data.detail || 'Erreur lors de la suppression');
      }
    } catch (e) {
      console.error('Erreur suppression:', e);
      toast.error('Erreur');
    }
  };
  
  // Filtrer les templates
  const filteredTemplates = templates.filter(t => 
    filterType === 'all' || t.type === filterType
  );
  
  const principalTemplates = filteredTemplates.filter(t => t.type === 'principal');
  const pipelineTemplates = filteredTemplates.filter(t => t.type === 'pipeline');
  
  return (
    <div className="space-y-6">
      {/* Header avec actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Building2 size={24} className="text-purple-400" />
            Templates de Modales
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Créez des templates pour différents métiers (MPA, Plomberie, Chauffage...)
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={loadTemplates}
            className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300"
            title="Rafraîchir"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg flex items-center gap-2"
          >
            <Plus size={16} />
            Nouveau Template
          </button>
        </div>
      </div>
      
      {/* Filtres */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-400">Filtrer :</span>
        {['all', 'principal', 'pipeline'].map(type => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              filterType === type
                ? 'bg-purple-500 text-white'
                : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {type === 'all' ? 'Tous' : type === 'principal' ? 'Principaux' : 'Pipeline'}
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-2">
          {filteredTemplates.length} template(s)
        </span>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
          <span className="ml-3 text-slate-400">Chargement...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Templates Principaux */}
          {(filterType === 'all' || filterType === 'principal') && principalTemplates.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                <FileText size={14} />
                Modales Principales
                <span className="text-xs bg-slate-700 px-2 py-0.5 rounded-full">{principalTemplates.length}</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {principalTemplates.map(template => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplateId === template.id}
                    onSelect={onSelectTemplate}
                    onSetDefault={setDefault}
                    onDuplicate={duplicate}
                    onDelete={deleteTemplate}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Templates Pipeline */}
          {(filterType === 'all' || filterType === 'pipeline') && pipelineTemplates.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                <Layers size={14} />
                Modales Pipeline
                <span className="text-xs bg-slate-700 px-2 py-0.5 rounded-full">{pipelineTemplates.length}</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pipelineTemplates.map(template => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplateId === template.id}
                    onSelect={onSelectTemplate}
                    onSetDefault={setDefault}
                    onDuplicate={duplicate}
                    onDelete={deleteTemplate}
                  />
                ))}
              </div>
            </div>
          )}
          
          {filteredTemplates.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun template trouvé</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-3 text-purple-400 hover:text-purple-300"
              >
                Créer votre premier template
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Modal de création */}
      <CreateTemplateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={createTemplate}
      />
    </div>
  );
}
